import { z } from "zod";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { useTranslation } from "react-i18next";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import { ControlledRadioGroup, useForm, useFormHandlers } from "../../../modules/form";
import { updateClaimDetails, usePreEligibilityDataSnapshot } from "../state/preEligibilityState";
import { SUPPORTED_INFORMED_BEFOREHAND } from "../../../settings/constants";
import { useFunnel } from "@airhelp/funnel";
import { useCreateClaimEnquiry } from "../../../modules/hooks/useCreateClaimEnquiry";
import { useInformedBeforehandOptions } from "../../../modules/hooks/useInformedBeforehandOptions";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { ControlledSelect } from "../../../modules/form/ControlledSelect";
import { getSelectOptions } from "./DelayInformation";

const defaultStepSchema = z.object({
  informedBeforehand: z.enum(SUPPORTED_INFORMED_BEFOREHAND).nullable(),
});

const stepSchema = z.object({
  informedBeforehand: z.enum(SUPPORTED_INFORMED_BEFOREHAND),
});

export function InformedBeforehandStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { claimDetails, itineraryFlights, flightDetails } = usePreEligibilityDataSnapshot();
  const { createClaimEnquiry } = useCreateClaimEnquiry();
  const isSaudi = claimDetails.potentialRegulation === "saudi_arabia";
  const isTrShy = claimDetails.potentialRegulation === "tr_shy";
  const isBrazil = claimDetails.potentialRegulation === "brazil";

  const {
    options: informedBeforehandOptions,
    optionsWithTranslations: informedBeforehandOptionsWithTranslations,
  } = useInformedBeforehandOptions({ potentialRegulation: claimDetails.potentialRegulation });

  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      informedBeforehand: claimDetails.informedBeforehand,
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    async onSubmit({ informedBeforehand }) {
      const updatedClaimDetails = updateClaimDetails({ informedBeforehand });

      if (funnel.currentStep?.meta?.claimShouldBeSubmitted) {
        const rawClaimEnquiry = await createClaimEnquiry({
          itineraryFlights,
          claimDetails: updatedClaimDetails,
          experiment: flightDetails.isFutureFlight ? "future_flight" : undefined,
        });
        trackClaimProcess({
          funnelStep: funnel.currentStep?.name,
          isEligible: rawClaimEnquiry?.claim_valid,
        });
        trackEnquiryStepComplete();
      } else {
        trackClaimProcess({ funnelStep: funnel.currentStep?.name });
        trackEnquiryStepComplete();
        funnel.stepForward();
      }
    },
  });

  const translationPrefix = () => {
    switch (true) {
      case isBrazil:
        return "steps.informed_beforehand.brazil";
      case isTrShy:
        return "steps.informed_beforehand.tr_shy";
      case isSaudi:
        return "steps.informed_beforehand.saudi_arabia";
      default:
        return "steps.informed_beforehand";
    }
  };

  return (
    <FunnelStepContainer data-testid={"InformedBeforehandDays"}>
      <FunnelStepSection title={t(`${translationPrefix()}.text`)}>
        {isSaudi ? (
          <ControlledSelect
            name="informedBeforehand"
            dataTestId="informedBeforehand"
            control={control}
            options={getSelectOptions(
              informedBeforehandOptions,
              informedBeforehandOptionsWithTranslations,
              t,
            )}
          />
        ) : (
          <ControlledRadioGroup
            name={"informedBeforehand"}
            control={control}
            options={informedBeforehandOptions}
            // @ts-ignore
            renderOption={(option) => t(informedBeforehandOptionsWithTranslations[option])}
          />
        )}
      </FunnelStepSection>
      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
